import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardActions, Button } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const services = [
  {
    id: 1,
    title: 'Web Development',
    description: 'Custom web applications tailored to your business needs using modern technologies and frameworks.',
    icon: CodeIcon,
    detailedDescription: 'Our web development services deliver custom solutions designed to meet your unique business requirements. Using the latest technologies including React, Angular, Node.js, and more, we create responsive, scalable, and user-friendly web applications that engage your customers and streamline your operations.',
    features: [
      'Responsive design for all devices',
      'Progressive Web Apps (PWA)',
      'E-commerce solutions',
      'Content Management Systems',
      'API development and integration'
    ]
  },
  {
    id: 2,
    title: 'Database Solutions',
    description: 'Design, development, and optimization of database systems to efficiently manage your data.',
    icon: StorageIcon,
    detailedDescription: 'Our database experts design and implement robust database solutions that ensure data integrity, security, and performance. Whether you need a new database architecture or optimization of existing systems, we provide comprehensive services to help you manage your data effectively.',
    features: [
      'Database design and modeling',
      'Data migration and integration',
      'Performance optimization',
      'SQL and NoSQL solutions',
      'Database maintenance and support'
    ]
  },
  {
    id: 3,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications for iOS and Android devices.',
    icon: PhoneIphoneIcon,
    detailedDescription: 'We develop high-performance, feature-rich mobile applications for iOS and Android platforms. Our mobile solutions are designed with a focus on user experience, performance, and scalability, helping businesses engage with their customers on the go.',
    features: [
      'Native iOS and Android development',
      'Cross-platform solutions (React Native, Flutter)',
      'UI/UX design for mobile',
      'App store submission assistance',
      'Ongoing maintenance and updates'
    ]
  },
  {
    id: 4,
    title: 'Cloud Services',
    description: 'Cloud migration, deployment, and management services to enhance scalability and accessibility.',
    icon: CloudIcon,
    detailedDescription: 'Our cloud services help businesses leverage the power of cloud computing to improve scalability, reduce costs, and enhance accessibility. We offer comprehensive cloud strategies, migration services, and ongoing management for AWS, Azure, and Google Cloud platforms.',
    features: [
      'Cloud migration strategy',
      'Infrastructure as Code (IaC)',
      'Serverless architecture',
      'DevOps implementation',
      'Cloud cost optimization'
    ]
  },
  {
    id: 5,
    title: 'Cybersecurity',
    description: 'Comprehensive security solutions to protect your systems, applications, and data from threats.',
    icon: SecurityIcon,
    detailedDescription: 'Our cybersecurity services help protect your business from evolving digital threats. We provide comprehensive security assessments, implement robust protection measures, and establish ongoing monitoring to ensure your systems and data remain secure.',
    features: [
      'Security assessments and audits',
      'Penetration testing',
      'Secure application development',
      'Security training for employees',
      'Incident response planning'
    ]
  },
  {
    id: 6,
    title: 'Data Analytics',
    description: 'Transform your raw data into actionable insights to drive informed business decisions.',
    icon: AnalyticsIcon,
    detailedDescription: 'Our data analytics services help you transform raw data into meaningful insights that drive business decisions. Using advanced analytics tools and techniques, we help you uncover patterns, trends, and correlations in your data that can lead to improved operations and strategic advantages.',
    features: [
      'Business intelligence dashboards',
      'Predictive analytics',
      'Big data processing',
      'Custom reporting solutions',
      'Data visualization'
    ]
  }
];

const Services = () => {
  return (
    <Box sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6, mb: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Our Services
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800 }}>
            Comprehensive IT solutions tailored to meet your business needs
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} elevation={2}>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                  <service.icon sx={{ fontSize: 60, color: 'primary.main' }} />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2" align="center">
                    {service.title}
                  </Typography>
                  <Typography variant="body1" align="justify">
                    {service.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button size="medium" color="primary">
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Why Choose Us Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" sx={{ mb: 5 }}>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'background.paper' }} elevation={1}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Expertise
                  </Typography>
                  <Typography variant="body1" align="justify">
                    Our team consists of highly skilled professionals with years of experience in their respective fields.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'background.paper' }} elevation={1}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Tailored Solutions
                  </Typography>
                  <Typography variant="body1" align="justify">
                    We create customized solutions that align perfectly with your business goals and requirements.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'background.paper' }} elevation={1}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Ongoing Support
                  </Typography>
                  <Typography variant="body1" align="justify">
                    We provide continuous support and maintenance to ensure your systems operate efficiently.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Services; 